import { useEffect, useState } from "react";
import CrudPage from "@/components/app/CrudPage";
import api, { errMsg, formatTL, formatNumber, formatDate, API } from "@/lib/api";
import { X, Wrench, FolderOpen, Route, Upload, Trash2, ExternalLink, Plus, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const emptyMaint = () => ({ date: new Date().toISOString().slice(0, 10), km: "", description: "", cost: "", notes: "" });

// Muayene / bakım tarihi durum rozeti: kırmızı = geçti, sarı = 30 gün içinde
const DueBadge = ({ date, km }) => {
  if (!date && !km) return <span className="text-xs text-neutral-400">-</span>;
  let cls = "text-neutral-600";
  let suffix = "";
  if (date) {
    const left = Math.ceil((new Date(date) - new Date()) / 86400000);
    if (left < 0) { cls = "text-white bg-[#DC2626] px-1.5 py-0.5 font-bold"; suffix = ` · ${Math.abs(left)}g geçti`; }
    else if (left <= 30) { cls = "text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 font-bold"; suffix = ` · ${left}g kaldı`; }
  }
  return (
    <span className={`inline-flex items-center text-[11px] num ${cls}`}>
      {date ? formatDate(date) : ""}{suffix}{!date && km ? `${formatNumber(km)} km` : ""}
    </span>
  );
};

export default function VehiclesPage() {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);        // seçili araç
  const [tab, setTab] = useState("maintenance");     // maintenance | files | km
  const [maint, setMaint] = useState(null);
  const [mForm, setMForm] = useState(emptyMaint());
  const [mEditing, setMEditing] = useState(null);
  const [summary, setSummary] = useState(null);
  const [kmLogs, setKmLogs] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  const openDetail = async (v, t = "maintenance") => {
    setDetail(v);
    setTab(t);
    setMForm(emptyMaint());
    setMEditing(null);
    loadDetail(v.id);
  };

  const loadDetail = async (vid) => {
    try {
      const [m, s, logs, vehicles] = await Promise.all([
        api.get(`/vehicles/${vid}/maintenance`),
        api.get(`/vehicles/${vid}/summary`),
        api.get("/km-logs"),
        api.get("/vehicles"),
      ]);
      setMaint(m.data);
      setSummary(s.data);
      setKmLogs((logs.data || []).filter((l) => l.vehicle_id === vid).sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 10));
      const fresh = (vehicles.data || []).find((x) => x.id === vid);
      if (fresh) setDetail(fresh);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitMaint = async (e) => {
    e.preventDefault();
    const body = {
      vehicle_id: detail.id,
      date: mForm.date,
      km: Number(mForm.km) || 0,
      description: mForm.description,
      cost: Number(mForm.cost) || 0,
      notes: mForm.notes || "",
    };
    try {
      if (mEditing) await api.put(`/vehicle-maintenance/${mEditing.id}`, body);
      else await api.post("/vehicle-maintenance", body);
      toast.success(mEditing ? "Bakım kaydı güncellendi" : "Bakım kaydı eklendi");
      setMForm(emptyMaint());
      setMEditing(null);
      loadDetail(detail.id);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteMaint = async (m) => {
    if (!window.confirm("Bakım kaydı silinsin mi?")) return;
    try { await api.delete(`/vehicle-maintenance/${m.id}`); toast.success("Silindi"); loadDetail(detail.id); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vehicles/${detail.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evrak yüklendi");
      loadDetail(detail.id);
      setReloadTick((t) => t + 1);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`"${f.original_filename}" silinsin mi?`)) return;
    try { await api.delete(`/vehicles/${detail.id}/files/${f.file_id}`); toast.success("Evrak silindi"); loadDetail(detail.id); setReloadTick((t) => t + 1); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const fileUrl = (f) => `${API}/vehicles/${detail.id}/files/${f.file_id}?auth=${localStorage.getItem("token") || ""}`;

  const TabBtn = ({ id, icon: Icon, label, count }) => (
    <button
      data-testid={`vehicle-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3.5 py-2 text-[10px] uppercase tracking-widest font-bold border-b-2 transition-colors ${tab === id ? "border-[#0020C2] text-[#0020C2]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}{count != null ? ` (${count})` : ""}
    </button>
  );

  return (
    <>
      <CrudPage
        key={reloadTick}
        title="Araçlar"
        subtitle="Filo araçları: teknik bilgiler, bakım geçmişi, evraklar ve km-yakıt özeti."
        endpoint="/vehicles"
        testidPrefix="vehicles"
        fields={[
          { key: "plate", label: "Plaka", type: "text", required: true },
          { key: "model", label: "Marka / Model", type: "text" },
          { key: "year", label: "Model Yılı", type: "number" },
          { key: "tank_capacity", label: "Depo Kapasitesi (L)", type: "number" },
          { key: "inspection_date", label: "Muayene Geçerlilik Tarihi", type: "date" },
          { key: "insurance_date", label: "Trafik Sigortası Bitiş", type: "date" },
          { key: "kasko_date", label: "Kasko Bitiş", type: "date" },
          { key: "next_maintenance_date", label: "Sonraki Bakım Tarihi", type: "date" },
          { key: "next_maintenance_km", label: "Sonraki Bakım KM", type: "number" },
          { key: "notes", label: "Notlar", type: "textarea" },
        ]}
        columns={[
          { key: "plate", label: "Plaka", render: (r) => <span className="num font-semibold">{r.plate}</span> },
          { key: "model", label: "Marka / Model" },
          { key: "year", label: "Yıl", align: "right" },
          { key: "inspection_date", label: "Muayene", render: (r) => <DueBadge date={r.inspection_date} /> },
          { key: "insurance_date", label: "Sigorta", render: (r) => <DueBadge date={r.insurance_date} /> },
          { key: "kasko_date", label: "Kasko", render: (r) => <DueBadge date={r.kasko_date} /> },
          { key: "next_maintenance_date", label: "Bakım", render: (r) => <DueBadge date={r.next_maintenance_date} km={r.next_maintenance_km} /> },
          {
            key: "_files", label: "Evrak", render: (r) => (r.files || []).length > 0
              ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#16A34A]"><FileText className="w-3.5 h-3.5" />{r.files.length}</span>
              : <span className="text-xs text-neutral-400">-</span>,
          },
          {
            key: "_detail", label: "", render: (r) => (
              <button
                data-testid={`vehicle-detail-${r.id}`}
                onClick={(e) => { e.stopPropagation(); openDetail(r); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors"
              >
                <Wrench className="w-3.5 h-3.5" /> Detay
              </button>
            ),
          },
        ]}
      />

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-3xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vehicle-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Araç Detayı</div>
                <div className="font-heading font-bold text-lg num">{detail.plate} <span className="text-sm font-normal text-neutral-500">{detail.model || ""}{detail.year ? ` · ${detail.year}` : ""}</span></div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex items-center gap-1 px-6 border-b border-[#F5F5F5]">
              <TabBtn id="maintenance" icon={Wrench} label="Bakım Kayıtları" count={maint?.count} />
              <TabBtn id="files" icon={FolderOpen} label="Evraklar" count={(detail.files || []).length} />
              <TabBtn id="km" icon={Route} label="Km & Yakıt" />
            </div>

            <div className="p-6">
              {/* BAKIM */}
              {tab === "maintenance" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#FAFAFA] border border-[#F5F5F5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Bakım Masrafı</div>
                      <div className="num font-bold text-lg text-[#DC2626]" data-testid="maint-total-cost">{formatTL(maint?.total_cost || 0)}</div>
                    </div>
                    <div className="bg-[#FAFAFA] border border-[#F5F5F5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bakım Sayısı</div>
                      <div className="num font-bold text-lg">{maint?.count || 0}</div>
                    </div>
                  </div>

                  <form onSubmit={submitMaint} className="border border-[#E5E5E5] p-4 space-y-3">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-600">{mEditing ? "Bakım Kaydını Düzenle" : "Yeni Bakım Kaydı"}</div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tarih *</label>
                        <input data-testid="maint-date" type="date" required value={mForm.date} onChange={(e) => setMForm({ ...mForm, date: e.target.value })} className="mt-1 w-full px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-sm" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Araç KM</label>
                        <input data-testid="maint-km" type="number" value={mForm.km} onChange={(e) => setMForm({ ...mForm, km: e.target.value })} placeholder="örn: 185000" className="mt-1 w-full px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-sm num" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Masraf (TL)</label>
                        <input data-testid="maint-cost" type="number" step="0.01" value={mForm.cost} onChange={(e) => setMForm({ ...mForm, cost: e.target.value })} placeholder="örn: 4500" className="mt-1 w-full px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-sm num" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Yapılan İşlemler *</label>
                      <textarea data-testid="maint-description" required rows={2} value={mForm.description} onChange={(e) => setMForm({ ...mForm, description: e.target.value })} placeholder="örn: Yağ + filtre değişimi, ön balatalar, rot balans" className="mt-1 w-full px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-sm" />
                    </div>
                    <div className="flex justify-end gap-2">
                      {mEditing && <button type="button" onClick={() => { setMEditing(null); setMForm(emptyMaint()); }} className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>}
                      <button data-testid="maint-submit" type="submit" className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">
                        <Plus className="w-3.5 h-3.5" /> {mEditing ? "Güncelle" : "Ekle"}
                      </button>
                    </div>
                  </form>

                  {!maint ? (
                    <div className="p-6 text-center text-sm text-neutral-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>
                  ) : maint.items.length === 0 ? (
                    <div className="p-6 text-center text-xs text-neutral-400 border border-dashed border-[#E5E5E5]">Henüz bakım kaydı yok.</div>
                  ) : (
                    <div className="overflow-x-auto border border-[#E5E5E5]">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FAFAFA]">
                          <tr>{["Tarih", "KM", "Yapılan İşlemler", "Masraf", ""].map((h, i) => <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {maint.items.map((m) => (
                            <tr key={m.id} className="border-t border-[#F5F5F5]" data-testid={`maint-row-${m.id}`}>
                              <td className="px-3 py-2 num">{formatDate(m.date)}</td>
                              <td className="px-3 py-2 num">{m.km ? formatNumber(m.km) : "-"}</td>
                              <td className="px-3 py-2 max-w-[280px]">{m.description}</td>
                              <td className="px-3 py-2 text-right num font-semibold">{formatTL(m.cost || 0)}</td>
                              <td className="px-3 py-2 text-right">
                                <div className="inline-flex gap-1">
                                  <button onClick={() => { setMEditing(m); setMForm({ date: m.date, km: m.km || "", description: m.description, cost: m.cost || "", notes: m.notes || "" }); }} className="p-1 hover:bg-[#F5F5F5]"><Wrench className="w-3.5 h-3.5" /></button>
                                  <button data-testid={`maint-delete-${m.id}`} onClick={() => deleteMaint(m)} className="p-1 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* EVRAKLAR */}
              {tab === "files" && (
                <div className="space-y-3">
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#E5E5E5] p-6 cursor-pointer hover:border-[#0020C2] hover:bg-[#F5F8FF] transition-colors text-sm text-neutral-600">
                    <Upload className="w-4 h-4" />
                    <span data-testid="vehicle-file-upload">Evrak Yükle (Ruhsat, Sigorta, Muayene, Kasko... — PDF, Excel, Word, Resim)</span>
                    <input type="file" accept=".pdf,.xlsx,.xls,.jpg,.jpeg,.png,.webp,.docx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }} />
                  </label>
                  {(detail.files || []).length === 0 ? (
                    <div className="p-6 text-center text-xs text-neutral-400">Henüz evrak yüklenmemiş.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {detail.files.map((f) => (
                        <div key={f.file_id} className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2.5 text-xs border border-[#F5F5F5]" data-testid={`vehicle-file-${f.file_id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
                            <div className="min-w-0">
                              <div className="truncate font-semibold">{f.original_filename}</div>
                              <div className="text-[10px] text-neutral-500 num">{(f.size / 1024).toFixed(0)} KB · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString("tr-TR") : ""}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <a href={fileUrl(f)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white border border-transparent hover:border-[#E5E5E5]"><ExternalLink className="w-3.5 h-3.5" /></a>
                            <button data-testid={`vehicle-file-delete-${f.file_id}`} onClick={() => deleteFile(f)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* KM & YAKIT */}
              {tab === "km" && (
                <div className="space-y-4">
                  {!summary ? (
                    <div className="p-6 text-center text-sm text-neutral-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          ["Toplam KM", formatNumber(summary.total_km)],
                          ["Toplam Yakıt (L)", formatNumber(summary.total_fuel)],
                          ["Ort. Tüketim (L/100km)", summary.avg_consumption],
                          ["Yakıt Maliyeti", formatTL(summary.total_cost)],
                        ].map(([l, v]) => (
                          <div key={l} className="bg-[#FAFAFA] border border-[#F5F5F5] p-3">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
                            <div className="num font-bold text-base mt-0.5">{v}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500">Son Km & Yakıt Kayıtları ({kmLogs.length})</div>
                        <button data-testid="goto-km-logs" onClick={() => navigate("/km-logs")} className="text-[10px] uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">Tüm Kayıtlar / Yeni Ekle →</button>
                      </div>
                      {kmLogs.length === 0 ? (
                        <div className="p-5 text-center text-xs text-neutral-400 border border-dashed border-[#E5E5E5]">Bu araç için km kaydı yok. "Km & Yakıt Kayıtları" sayfasından ekleyebilirsiniz.</div>
                      ) : (
                        <div className="overflow-x-auto border border-[#E5E5E5]">
                          <table className="w-full text-xs">
                            <thead className="bg-[#FAFAFA]">
                              <tr>{["Tarih", "Başlangıç KM", "Bitiş KM", "Mesafe", "Yakıt (L)", "Maliyet"].map((h, i) => <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                              {kmLogs.map((l) => (
                                <tr key={l.id} className="border-t border-[#F5F5F5]">
                                  <td className="px-3 py-2 num">{formatDate(l.date)}</td>
                                  <td className="px-3 py-2 text-right num">{formatNumber(l.km_start)}</td>
                                  <td className="px-3 py-2 text-right num">{formatNumber(l.km_end)}</td>
                                  <td className="px-3 py-2 text-right num font-semibold">{formatNumber(l.distance || (l.km_end - l.km_start))}</td>
                                  <td className="px-3 py-2 text-right num">{formatNumber(l.fuel_liters)}</td>
                                  <td className="px-3 py-2 text-right num">{formatTL(l.fuel_cost || 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
